/**
 * Button and IconButton.
 *
 * WHICH VARIANT (docs/design/DESIGN-SYSTEM.md, "Buttons"):
 *
 *   primary       moonlight. The one thing this view is for. ONE per view.
 *   secondary     the default. Anything else you can do here.
 *   quiet         text until hovered. Cancel, dismiss, a toolbar action.
 *   destructive   amber on an amber tint, never solid. Deletes or discards.
 *   paper         solid teal. Starts a SIMULATED run, and nothing else.
 *   live          a red outline: armed, not hot. Always opens a confirmation.
 *   live-solid    solid red. Exists only inside the live confirmation dialog.
 *
 * Red is never a destructive button, and teal is never a "success" button:
 * those two colours say which money is in play and nothing else.
 *
 * `loading` keeps the button focusable (a disabled button drops focus to the
 * page, which loses a keyboard user's place) and swallows clicks instead. With
 * a `loadingLabel` the button says what it is doing ("Starting…") and is
 * exactly as wide as the wider of its two labels, so it never jumps.
 *
 * `asChild` draws the button as its single child element, for a router
 * `<Link>` or an `<a>` that must look like a button but navigate like a link.
 */
import type { ButtonHTMLAttributes, MouseEvent, ReactNode, Ref } from "react";
import { Slot, Slottable } from "radix-ui/slot";
import { cx } from "./cx.js";
import { Spinner } from "./Spinner.js";
import "./button.css";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "quiet"
  | "destructive"
  | "paper"
  | "live"
  | "live-solid";

/** sm 28px, md 36px (default), lg 46px. */
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon: `<Icon as={Plus} />`. Replaced by a spinner while loading. */
  icon?: ReactNode;
  /** Trailing icon or key hint: `<Kbd>N</Kbd>`. */
  trailing?: ReactNode;
  /** Work is in flight. Sets `aria-busy`, ignores clicks, stays focusable. */
  loading?: boolean;
  /** What the button says while loading: "Saving…". Keeps the width stable. */
  loadingLabel?: string;
  /** Stretch to the container's width. */
  block?: boolean;
  /** Render as the single child element instead of a `<button>`. */
  asChild?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

export function Button({
  variant = "secondary",
  size = "md",
  icon,
  trailing,
  loading = false,
  loadingLabel,
  block = false,
  asChild = false,
  className,
  children,
  onClick,
  type,
  ...rest
}: ButtonProps) {
  const Root = asChild ? Slot : "button";
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  const swap = loading && loadingLabel !== undefined;

  return (
    <Root
      {...(asChild ? {} : { type: type ?? "button" })}
      {...rest}
      aria-busy={loading || undefined}
      aria-disabled={loading || rest["aria-disabled"] || undefined}
      data-variant={variant}
      data-size={size}
      data-loading={loading ? "true" : undefined}
      data-block={block ? "true" : undefined}
      className={cx("mdl-btn", className)}
      onClick={handleClick}
    >
      {/* `Slottable` has to be a direct child: with `asChild`, Radix looks for
          it among this element's children and no deeper. */}
      {loadingLabel === undefined && (loading ? <Spinner /> : icon)}
      {loadingLabel === undefined ? (
        <Slottable>{children}</Slottable>
      ) : (
        // Both labels share one grid cell, so the width is the wider of the two.
        <span className="mdl-btn__swap">
          <span data-shown={swap ? "false" : "true"} aria-hidden={swap || undefined}>
            {icon}
            {children}
          </span>
          <span data-shown={swap ? "true" : "false"} aria-hidden={!swap || undefined}>
            <Spinner />
            {loadingLabel}
          </span>
        </span>
      )}
      {/* The space is for the accessible name ("New strategy N", not
          "New strategyN"); a flex row does not draw it. */}
      {trailing !== undefined && trailing !== null && " "}
      {trailing}
    </Root>
  );
}

export interface IconButtonProps
  extends Omit<ButtonProps, "children" | "icon" | "trailing" | "loadingLabel" | "block"> {
  /** The accessible name. An icon alone names nothing. */
  label: string;
  /** `<Icon as={Undo2} />` */
  children: ReactNode;
}

/**
 * A square button that is only an icon. `label` is required and becomes the
 * accessible name; wrap it in `<Tooltip>` so a sighted mouse user is told the
 * same thing.
 */
export function IconButton({
  label,
  children,
  variant = "quiet",
  loading,
  className,
  ...rest
}: IconButtonProps) {
  return (
    <Button
      {...rest}
      variant={variant}
      {...(loading === undefined ? {} : { loading })}
      aria-label={label}
      className={cx("mdl-btn--icon", className)}
    >
      {loading ? null : children}
    </Button>
  );
}
